import React, { useContext, useEffect, useMemo } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { makeStyles } from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  withStyles,
} from '@material-ui/core';
import DatePicker from 'components/CustomInputs/DatePicker';
import { useActiveUnbalancedContractsToDate } from 'hooks';
import { Closure, SortingCenterContractsEditFormPutBatch } from 'types';
import { colors, fonts } from 'theme';
import { useLatestClosureDate } from 'utils/closureHooks';
import { createUTCDateFromDateString } from 'utils/date';
import { AppContext } from 'context';
import { sortingCenterContractsEditFormSchema } from './SortingCenterContractsEditForm.yup';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    paddingBottom: '2.5em',
  },
  sortingCenterContractsTable: {
    width: '100%',
  },
  headerCell: {
    paddingTop: '40px',
    paddingBottom: '50px',
    padding: ' 10px 11px',
  },
  bodyCell: {
    padding: '6px 22px',
    border: `2px solid ${colors.greyLighten20}`,
    maxHeight: 3,
    '&:first-child': {
      borderLeft: 'none',
    },
    '&:last-child': {
      borderRight: 'none',
    },
  },
  clientNameCell: {
    width: '25%',
  },
  streamNameCell: {
    width: '30%',
  },
  nbCarac: {
    width: '25%',
  },
  endDate: {
    width: '20%',
  },
  nbCaracField: {
    marginLeft: '60%',
  },
  endDateField: {
    marginLeft: '20%',
  },
  muiTextFieldRoot: {
    '& input': {
      textAlign: 'right',
      fontFamily: fonts.secondary,
    },
  },
  alignLeft: {
    textAlign: 'left',
  },
  alignCenter: {
    textAlign: 'center',
  },
});

const StyledTableCell = withStyles({
  root: {
    fontFamily: fonts.secondary,
    fontSize: '16px',
  },
})(TableCell);

interface SortingCenterContractsFormProps {
  onSubmit: SubmitHandler<SortingCenterContractsEditFormPutBatch>;
  formName: string;
  closure: Closure;
}

const SortingCenterContractsForm: React.FC<SortingCenterContractsFormProps> = ({ onSubmit, formName, closure }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'clientContractsEditForm' });
  const classes = useStyles();
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const closureDate = closure ? new Date(Date.UTC(closure.year, closure.month, 0)) : null;
  const activeContracts = useActiveUnbalancedContractsToDate(closureDate, undefined, undefined, true);
  const [lastClosureDate] = useLatestClosureDate(sortingCenter);
  const initializationDate = useMemo(
    () => (sortingCenter ? new Date(sortingCenter.initializationDate) : new Date()),
    [sortingCenter],
  );
  const schema = useMemo(
    () => sortingCenterContractsEditFormSchema(t, lastClosureDate ?? initializationDate),
    [t, lastClosureDate, initializationDate],
  );

  const { control, handleSubmit, reset } = useForm<SortingCenterContractsEditFormPutBatch>({
    mode: 'onBlur',
    resolver: yupResolver(schema),
  });

  activeContracts?.sort((a, b) => {
    // Trier par ordre alphabétique du nom du client
    const clientComparison = a.client.shortName.localeCompare(b.client.shortName);
    if (clientComparison !== 0) {
      return clientComparison;
    }

    // Trier par ordre croissant de la date de fin
    return createUTCDateFromDateString(a.endDate).getTime() - createUTCDateFromDateString(b.endDate).getTime();
  });

  useEffect(() => {
    if (activeContracts) {
      const defaultValues = {
        activeContracts: activeContracts.map(activeContract => ({
          ...activeContract,
          id: activeContract['@id'],
          endDate: createUTCDateFromDateString(activeContract.endDate),
          initialEndDate: createUTCDateFromDateString(activeContract.endDate),
          startDate: createUTCDateFromDateString(activeContract.startDate),
        })),
      };

      reset(defaultValues);
    }
  }, [activeContracts, reset]);

  return (
    <>
      <form id={formName} onSubmit={handleSubmit(onSubmit)} className={classes.form}>
        {activeContracts && (
          <TableContainer className={classes.sortingCenterContractsTable}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell className={`${classes.headerCell} ${classes.clientNameCell}  ${classes.alignLeft}`}>
                    {t('clientName')}
                  </StyledTableCell>
                  <StyledTableCell className={`${classes.headerCell} ${classes.clientNameCell} ${classes.alignLeft}`}>
                    {t('streamName')}
                  </StyledTableCell>
                  <StyledTableCell className={`${classes.headerCell} ${classes.nbCarac} ${classes.alignCenter}`}>
                    {t('nbCarac')}
                  </StyledTableCell>
                  <StyledTableCell className={`${classes.headerCell} ${classes.endDate} ${classes.alignCenter}`}>
                    {t('endDate')}
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeContracts.map((activeContract, index) => {
                  const isClientChanged =
                    index === 0 || activeContract.client.shortName !== activeContracts[index - 1].client.shortName;

                  return (
                    <TableRow key={`activeContract.${index}`}>
                      <StyledTableCell className={`${classes.bodyCell} ${classes.clientNameCell}`}>
                        {isClientChanged ? activeContract.client.shortName : ''}
                      </StyledTableCell>
                      <StyledTableCell className={`${classes.bodyCell} ${classes.streamNameCell}`}>
                        {activeContract.stream.name}
                      </StyledTableCell>
                      <StyledTableCell className={`${classes.bodyCell} ${classes.nbCarac} ${classes.alignCenter}`}>
                        <Controller
                          name={`activeContracts.${index}.characterizationNumber`}
                          control={control}
                          render={({ field: { ref, onBlur, ...inputProps }, fieldState }) => (
                            <TextField
                              className={`${classes.muiTextFieldRoot} ${classes.nbCaracField}`}
                              inputRef={ref}
                              value={inputProps.value || ''}
                              onBlur={onBlur}
                              onChange={event => {
                                if (Number(event.target.value) >= 0) {
                                  inputProps.onChange(Number(event.target.value));
                                }
                              }}
                              helperText={fieldState.error?.message}
                              error={fieldState.invalid}
                            />
                          )}
                        />
                      </StyledTableCell>
                      <StyledTableCell className={`${classes.bodyCell} ${classes.endDate} ${classes.alignCenter}`}>
                        <Controller
                          name={`activeContracts.${index}.endDate`}
                          control={control}
                          render={({ field: { ref, onBlur, ...fieldProps }, fieldState }) => (
                            <DatePicker
                              className={`${classes.muiTextFieldRoot} ${classes.endDateField}`}
                              error={fieldState.invalid}
                              helperText={fieldState.error?.message}
                              clearLabel={t('delete')}
                              cancelLabel={t('cancel')}
                              inputVariant="standard"
                              format={'dd/MM/yyyy'}
                              onBlur={onBlur}
                              disableFuture={false}
                              {...fieldProps}
                            />
                          )}
                        />
                      </StyledTableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </form>
    </>
  );
};

export default SortingCenterContractsForm;
